import {
  cardId,
  createRng,
  IllegalMoveError,
  standardDeck,
  type CardId,
  type GameEngine,
  type GameOptions,
  type RNG,
  type Seat,
} from "@bing/engine-core";
import {
  assertPasurSeats,
  captureKey,
  captureOptions,
  DEFAULT_TARGET_SCORE,
  isJack,
  isSurCapped,
  openingTableVerdict,
  scoreDeal,
  sideOf,
  SIDES,
  tallyDeal,
} from "./rules";
import {
  PASUR_RULES_VERSION,
  type PasurDealResult,
  type PasurMatchDetail,
  type PasurMove,
  type PasurPlay,
  type PasurState,
  type PasurView,
} from "./types";

/** Cards each seat is dealt a round (the opening round and every refill). */
export const HAND_SIZE = 4;
/** Cards the opening round lays face-up on the table — the only time a deal lays any. */
export const TABLE_SIZE = 4;

/**
 * How many times one deal's opening table may be reshuffled before the deterministic fallback lays
 * it instead. A reshuffle happens to ≈ 1 opening in 37, so 64 in a row is ≈ 10⁻¹⁰⁰: unreachable.
 */
export const MAX_RESHUFFLES = 64;

/**
 * How many Jacks one opening lay may bury before it is treated like a bad table and reshuffled.
 * Without a buried Jack coming back there are at most 4 burials (4 Jacks); every burial past that
 * needs a buried Jack to land on top of the pack (≈ 1 in 33) AND be drawn as the next replacement,
 * so 8 is never reached in practice — but the loop must END by code, not by an invariant (D6b).
 */
export const MAX_BURIES = 8;

const FULL_DECK: readonly CardId[] = standardDeck().map(cardId);

/*
 * THE TABLE WAITS FOR WHAT IT SHOWS (P1 Task 6, R6). The rooms hold the next BOT move for
 * `postMoveDelayMs` of the state (the solo room and the online table alike), and the game-server's
 * scripted beat holds the next TURN WINDOW — and its `turn` frame — for the same span
 * (`apps/game-server/src/rooms/scripted-beats-pasur.ts`): while the web table shows one of these, the
 * seat on turn has no control on screen.
 *
 * Each beat is EXACTLY the web's span for what it covers (`apps/web/src/game/pasur-moments.ts`) plus
 * PASUR_BEAT_MARGIN_MS. The engine cannot import the web, so the spans are restated here, and
 * `test/pacing.test.ts` IMPORTS the web's and fails the moment one drifts — KEEP THEM IN LOCKSTEP.
 */
/** R6: a beat outlasts what the table shows by this much — a frame's travel, and a breath. */
export const PASUR_BEAT_MARGIN_MS = 150;
/** A capture on screen — the played card's flight onto its set, the hold, the set's slide (the web's
 *  `PASUR_CAPTURE_MS`; a سور and its cancel are captures, `PASUR_SUR_MS`). */
const CAPTURE_SPAN_MS = 700 + 400;
/** The deal's remainder sliding to the last capturer (`PASUR_SCOOP_MS`). */
const SCOOP_SPAN_MS = 700 + 500;
/** The per-deal breakdown card after the scoop (`PASUR_BREAKDOWN_MS`, Task 7's card). */
const BREAKDOWN_SPAN_MS = 3000;
/** The deal's rhythm out of the stock (`PASUR_DEAL_CARD_MS`, `_PACKET_MS`, `_FLIGHT_MS`). */
const DEAL_CARD_MS = 60;
const DEAL_PACKET_MS = 120;
const DEAL_FLIGHT_MS = 340;
/** One buried Jack: its flight, its stay on the table, its return into the stock
 *  (`PASUR_DEAL_FLIGHT_MS` + `PASUR_BURY_SHOW_MS` + `PASUR_BURY_RETURN_MS`). */
const BURIAL_SPAN_MS = DEAL_FLIGHT_MS + 500 + 340;

/** A deal out of the stock: `packets` packets of four (each seat's, then an opening's table), then
 *  each burial (the web's `pasurDealMs`). */
function dealSpanMs(packets: number, burials: number): number {
  const last = packets * HAND_SIZE - 1;
  return (
    last * DEAL_CARD_MS +
    Math.floor(last / HAND_SIZE) * DEAL_PACKET_MS +
    DEAL_FLIGHT_MS +
    burials * BURIAL_SPAN_MS
  );
}

/** What a beat covers: the match's first deal · a deal's end, its card and the next deal · a refill
 *  round (after the round's last play) · a سور or its cancel (a capture that cleared the table, and its
 *  announce) · a plain capture. The game-server's scripted beat holds the turn window for every kind
 *  but the plain capture (the controller's ruling, 2026-10-11 — `scripted-beats-pasur.ts`). */
export type PasurBeatKind = "opening" | "dealEnd" | "refill" | "sur" | "capture";

/** The PUBLIC facts a beat is read from — on the state and on every seat's view alike (the
 *  game-server's scripted beat reads the view of the seat on turn). */
export type PasurBeatFacts = Pick<
  PasurState,
  "phase" | "seats" | "dealNumber" | "playLog" | "lastDeal" | "buried"
>;

/**
 * THE BEAT A STATE IS — what the web table is showing as it arrives, and for how long the next move
 * must wait for it (the span + PASUR_BEAT_MARGIN_MS); null when it brings nothing to wait for (a plain
 * trail, whose 400 ms flight the rooms' flat 700 ms cadence already covers) or the match is over.
 * Read off PUBLIC fields only, so a seat's view and the state can never disagree.
 */
export function pasurBeat(s: PasurBeatFacts): { kind: PasurBeatKind; ms: number } | null {
  if (s.phase !== "playing") return null;
  const log = s.playLog;
  if (log.length === 0) {
    const opening = dealSpanMs(s.seats + 1, s.buried.length);
    const deal = s.lastDeal;
    if (deal === null) return { kind: "opening", ms: opening + PASUR_BEAT_MARGIN_MS };
    // the deal's end as the table draws it: its last capture's hold and slide, then the remainder's
    // scoop (a trailed last card lands with it; with no remainder, no scoop)
    const play = deal.finalPlay;
    const end =
      (play.capture.length > 0 ? CAPTURE_SPAN_MS : 0) +
      (deal.scoop !== null || play.capture.length === 0 ? SCOOP_SPAN_MS : 0);
    return { kind: "dealEnd", ms: end + BREAKDOWN_SPAN_MS + opening + PASUR_BEAT_MARGIN_MS };
  }
  const last = log[log.length - 1]!;
  const captured = last.capture.length > 0 ? CAPTURE_SPAN_MS : 0;
  // every hand empty and the stock not: the round's last play, then the next round out of the stock
  if (log.length % (s.seats * HAND_SIZE) === 0)
    return { kind: "refill", ms: captured + dealSpanMs(s.seats, 0) + PASUR_BEAT_MARGIN_MS };
  if (captured === 0) return null;
  // a سور (or its cancel) is a capture's hold too — and an announce: its own kind
  return { kind: last.sur === undefined ? "capture" : "sur", ms: captured + PASUR_BEAT_MARGIN_MS };
}

function emptyPerSeat(seats: number): CardId[][] {
  return Array.from({ length: seats }, () => []);
}

/** One round: 4 cards to each seat, in turn order starting at `dealer + 1`, off the front of `deck`. */
function dealRound(
  deck: readonly CardId[],
  seats: number,
  dealer: Seat,
): { hands: CardId[][]; rest: CardId[] } {
  const hands = emptyPerSeat(seats);
  for (let i = 0; i < seats; i++) {
    hands[(dealer + 1 + i) % seats] = deck.slice(i * HAND_SIZE, (i + 1) * HAND_SIZE);
  }
  return { hands, rest: deck.slice(seats * HAND_SIZE) };
}

/** A deal's opening round, D6 applied. */
export interface PasurOpening {
  readonly hands: CardId[][];
  readonly table: CardId[];
  readonly deck: CardId[];
  readonly buried: CardId[];
  readonly reshuffles: number;
}

/**
 * The bound was hit (never in practice): lay the table from the dealt order, taking each card that
 * keeps it legal (no Jack, < 3 Queens, < 3 Kings) and moving every skipped card to the back of the
 * deck. Deterministic, conserves all 52 cards, and always completes: at least 24 number cards are
 * left outside the hands.
 */
function fallbackTable(rest: readonly CardId[]): { table: CardId[]; deck: CardId[] } {
  const table: CardId[] = [];
  const skipped: CardId[] = [];
  let i = 0;
  for (; i < rest.length && table.length < TABLE_SIZE; i++) {
    const card = rest[i]!;
    if (openingTableVerdict([...table, card]) === "ok") table.push(card);
    else skipped.push(card);
  }
  return { table, deck: [...rest.slice(i), ...skipped] };
}

/**
 * Deal a deal's opening round from `rng`: shuffle all 52 cards, 4 to each seat starting after the
 * `dealer`, then 4 to the table. D6, card-91's refinement — the table is re-judged after EVERY
 * replacement: ≥ 2 Jacks, ≥ 3 Queens or ≥ 3 Kings reshuffle the whole deck from the same `rng`;
 * exactly one Jack is BURIED: the deck's top card takes its place on the table, then the Jack goes
 * back into the undealt deck at a seeded random place (D6b, card-91: uniform over every position,
 * the top and the bottom included, drawn from the same `rng`); otherwise the table stands.
 *
 * A Jack buried on top can be drawn straight back as the next replacement, so the burial loop is
 * capped at {@link MAX_BURIES} by code; past it the lay is reshuffled like a bad table. The
 * reshuffles are bounded by `maxReshuffles`, with a deterministic fallback.
 */
export function openDeal(
  seats: number,
  dealer: Seat,
  rng: RNG,
  maxReshuffles: number = MAX_RESHUFFLES,
): PasurOpening {
  for (let reshuffles = 0; ; reshuffles++) {
    const { hands, rest } = dealRound(rng.shuffle(FULL_DECK), seats, dealer);
    const table = rest.slice(0, TABLE_SIZE);
    let deck = rest.slice(TABLE_SIZE);
    const buried: CardId[] = [];
    let verdict = openingTableVerdict(table);
    while (verdict === "bury" && buried.length < MAX_BURIES) {
      const at = table.findIndex(isJack);
      const jack = table[at]!;
      table[at] = deck[0]!;
      deck = deck.slice(1);
      const place = rng.int(deck.length + 1); // 0 = the top … deck.length = the bottom
      deck = [...deck.slice(0, place), jack, ...deck.slice(place)];
      buried.push(jack);
      verdict = openingTableVerdict(table);
    }
    // ≥ 2 J / ≥ 3 Q / ≥ 3 K — or a lay still burying at the cap — is reshuffled.
    if (verdict === "ok") return { hands, table, deck, buried, reshuffles };
    if (reshuffles >= maxReshuffles) {
      return { hands, ...fallbackTable(rest), buried: [], reshuffles };
    }
  }
}

/** What carries from deal to deal. */
type MatchFields = Pick<
  PasurState,
  "rulesVersion" | "seats" | "totalScores" | "targetScore" | "lastDeal"
>;

/** A fresh deal `dealNumber` dealt by `dealer`: new piles, no سور, an empty log, the seat after
 *  the dealer to lead. Draws from `rng` and carries its state forward. */
function startDeal(match: MatchFields, dealNumber: number, dealer: Seat, rng: RNG): PasurState {
  const opening = openDeal(match.seats, dealer, rng);
  return {
    rulesVersion: match.rulesVersion,
    seats: match.seats,
    phase: "playing",
    dealNumber,
    dealer,
    hands: opening.hands,
    table: opening.table,
    deck: opening.deck,
    captured: emptyPerSeat(match.seats),
    surs: Array.from({ length: SIDES }, () => 0),
    turn: (dealer + 1) % match.seats,
    lastCapturer: null,
    playLog: [],
    buried: opening.buried,
    reshuffles: opening.reshuffles,
    totalScores: match.totalScores,
    targetScore: match.targetScore,
    lastDeal: match.lastDeal,
    rngState: rng.getState(),
  };
}

/**
 * The deal is over (every hand empty, the deck empty): the last capturer takes the table (D14,
 * never a سور; if nobody captured all deal it stays unowned and scores for no one), the deal is
 * scored per side, and the totals grow. The match ends when the top total reaches the target AND
 * is unique (D16); otherwise the next deal is dealt by the next dealer (D18).
 */
function finishDeal(state: PasurState, finalPlay: PasurPlay): PasurState {
  let captured = state.captured;
  let table = state.table;
  let scoop: PasurDealResult["scoop"] = null;
  if (table.length > 0 && state.lastCapturer !== null) {
    const taker = state.lastCapturer;
    scoop = { seat: taker, cards: [...table] };
    captured = captured.map((pile, seat) => (seat === taker ? [...pile, ...table] : pile));
    table = [];
  }
  const tallies = tallyDeal(captured, state.surs, state.seats);
  const points = scoreDeal(tallies);
  const totalScores = state.totalScores.map((total, side) => total + (points[side] ?? 0));
  const lastDeal: PasurDealResult = {
    dealNumber: state.dealNumber,
    dealer: state.dealer,
    tallies,
    points,
    totalsAfter: [...totalScores],
    finalPlay,
    scoop,
  };
  const top = Math.max(...totalScores);
  if (top >= state.targetScore && totalScores.filter((t) => t === top).length === 1) {
    return { ...state, phase: "gameOver", captured, table, totalScores, lastDeal };
  }
  return startDeal(
    { ...state, totalScores, lastDeal },
    state.dealNumber + 1,
    (state.dealer + 1) % state.seats,
    createRng(state.rngState),
  );
}

function copyPlay(play: PasurPlay): PasurPlay {
  return { ...play, capture: [...play.capture] };
}

function copyDealResult(deal: PasurDealResult): PasurDealResult {
  return {
    ...deal,
    tallies: deal.tallies.map((tally) => ({ ...tally })),
    points: [...deal.points],
    totalsAfter: [...deal.totalsAfter],
    finalPlay: copyPlay(deal.finalPlay),
    scoop: deal.scoop && { seat: deal.scoop.seat, cards: [...deal.scoop.cards] },
  };
}

/**
 * The engine half of resuming an old save: TRUE only for a state made under these rules. A row
 * persisted by the old engine (no `rulesVersion`) must be ended cleanly on load, recording nothing
 * as a win or a loss — it is {@link pasurEngine}'s `canResume`, which the solo room asks.
 */
export function isCurrentPasurRules(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { rulesVersion?: unknown }).rulesVersion === PASUR_RULES_VERSION
  );
}

export const pasurEngine: GameEngine<PasurState, PasurMove, PasurView> = {
  gameId: "pasur",

  /**
   * A save the old engine wrote (four individuals, one deal, no `rulesVersion`) cannot be played by
   * these rules — its view would throw. Answering false makes the solo room retire that row on
   * «ادامه بازی»: deleted, recorded as nothing, the player told with `SAVED_GAME_RETIRED_MESSAGE`
   * (card-91, R2). Pure, and it tolerates any persisted JSON.
   */
  canResume: isCurrentPasurRules,

  createInitialState(opts: GameOptions, rng: RNG): PasurState {
    const { seats } = opts;
    assertPasurSeats(seats);
    const target = opts.rules?.["targetScore"];
    const targetScore =
      typeof target === "number" && Number.isFinite(target) && target > 0
        ? target
        : DEFAULT_TARGET_SCORE;
    const match: MatchFields = {
      rulesVersion: PASUR_RULES_VERSION,
      seats,
      totalScores: Array.from({ length: SIDES }, () => 0),
      targetScore,
      lastDeal: null,
    };
    // Deal 0 is dealt by the last seat, so seat 0 leads the match.
    return startDeal(match, 0, seats - 1, rng);
  },

  legalMoves(state: PasurState, seat: Seat): PasurMove[] {
    if (state.phase !== "playing" || seat !== state.turn) return [];
    const moves: PasurMove[] = [];
    for (const card of state.hands[seat]!) {
      const options = captureOptions(card, state.table);
      if (options.length === 0) {
        moves.push({ card, capture: [] }); // no capture possible → trail
      } else {
        for (const capture of options) moves.push({ card, capture });
      }
    }
    return moves;
  },

  isLegal(state: PasurState, seat: Seat, move: PasurMove): boolean {
    if (state.phase !== "playing" || seat !== state.turn) return false;
    if (!state.hands[seat]!.includes(move.card)) return false;
    const options = captureOptions(move.card, state.table);
    // D5: a card that CAN capture must — trailing it is illegal. (You may still choose to play a
    // different, non-capturing card instead.)
    if (move.capture.length === 0) return options.length === 0;
    const key = captureKey(move.capture);
    return options.some((option) => captureKey(option) === key);
  },

  applyMove(state: PasurState, seat: Seat, move: PasurMove): PasurState {
    if (!pasurEngine.isLegal(state, seat, move)) throw new IllegalMoveError(seat, move);
    const { card, capture } = move;

    const hands = state.hands.map((hand, i) =>
      i === seat ? hand.filter((c) => c !== card) : hand,
    );
    let table: readonly CardId[];
    let captured = state.captured;
    let lastCapturer = state.lastCapturer;
    let surs = state.surs;
    let sur: PasurPlay["sur"];

    if (capture.length > 0) {
      const taken = new Set(capture);
      table = state.table.filter((c) => !taken.has(c));
      captured = state.captured.map((pile, i) => (i === seat ? [...pile, card, ...capture] : pile));
      lastCapturer = seat;
      // سور: a capture that clears the table — never with a Jack (D7), never in the final round
      // (D8: the deck is already empty), never by a side at the cap (D11: neither branch below), and
      // the deal's very first play may make one (D9).
      const mine = sideOf(seat);
      if (
        table.length === 0 &&
        !isJack(card) &&
        state.deck.length > 0 &&
        !isSurCapped(state, mine)
      ) {
        const theirs = (mine + 1) % SIDES;
        // D10: a سور against a side holding سورs cancels one of theirs instead of scoring.
        if ((surs[theirs] ?? 0) > 0) {
          surs = surs.map((n, side) => (side === theirs ? n - 1 : n));
          sur = "cancelled";
        } else {
          surs = surs.map((n, side) => (side === mine ? n + 1 : n));
          sur = "scored";
        }
      }
    } else {
      table = [...state.table, card];
    }

    const play: PasurPlay = sur
      ? { seat, card, capture: [...capture], sur }
      : { seat, card, capture: [...capture] };
    const next: PasurState = {
      ...state,
      hands,
      table,
      captured,
      surs,
      lastCapturer,
      playLog: [...state.playLog, play],
      turn: (seat + 1) % state.seats,
    };

    if (hands.some((hand) => hand.length > 0)) return next;
    if (state.deck.length > 0) {
      // Every hand is empty: the next round, 4 each from the seat after the dealer.
      const round = dealRound(state.deck, state.seats, state.dealer);
      return { ...next, hands: round.hands, deck: round.rest };
    }
    return finishDeal(next, play);
  },

  isTerminal(state: PasurState): boolean {
    return state.phase === "gameOver";
  },

  /**
   * The rooms' pacing hint (R6): how long the next move waits for what the table is showing — a
   * capture, a refill round, a deal's end and the next deal, the match's first deal ({@link pasurBeat});
   * undefined for a plain trail (the rooms' flat cadence) and at the match's end. Pure.
   */
  postMoveDelayMs(state: PasurState): number | undefined {
    return pasurBeat(state)?.ms;
  },

  /**
   * The match result, per side (حکم's shape): every seat scores its side's total, `teamScores` are
   * the side totals, and `winners` lists every seat of the leading side. A finished match always
   * has a unique leader (D16), so `tied` is never set. Meaningful at `gameOver`; mid-match it does
   * not throw, and `detail` appears once the first deal has been scored.
   */
  score(state: PasurState) {
    const teamScores = [...state.totalScores];
    const best = Math.max(...teamScores);
    const seats = Array.from({ length: state.seats }, (_unused, seat) => seat);
    const seatScores = seats.map((seat) => teamScores[sideOf(seat)] ?? 0);
    const winners = seats.filter((seat) => teamScores[sideOf(seat)] === best);
    if (state.lastDeal === null) return { seatScores, teamScores, winners };
    const detail: PasurMatchDetail = {
      dealsPlayed: state.lastDeal.dealNumber + 1,
      targetScore: state.targetScore,
      totalScores: [...state.totalScores],
      lastDeal: copyDealResult(state.lastDeal),
    };
    return { seatScores, teamScores, winners, detail };
  },

  viewFor(state: PasurState, seat: Seat): PasurView {
    return {
      seats: state.seats,
      phase: state.phase,
      you: seat,
      yourSide: sideOf(seat),
      dealNumber: state.dealNumber,
      dealer: state.dealer,
      hand: [...state.hands[seat]!],
      handCounts: state.hands.map((hand) => hand.length),
      table: [...state.table],
      deckCount: state.deck.length,
      finalRound: state.deck.length === 0 && state.hands.some((hand) => hand.length > 0),
      yourCaptured: [...state.captured[seat]!],
      capturedCounts: state.captured.map((pile) => pile.length),
      tallies: tallyDeal(state.captured, state.surs, state.seats),
      surs: [...state.surs],
      turn: state.turn,
      lastCapturer: state.lastCapturer,
      playLog: state.playLog.map(copyPlay),
      buried: [...state.buried],
      reshuffles: state.reshuffles,
      totalScores: [...state.totalScores],
      targetScore: state.targetScore,
      lastDeal: state.lastDeal && copyDealResult(state.lastDeal),
    };
  },
};
